import { Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { memo } from 'react';
import { useAppDispatch } from 'redux/hooks';
import { setMachineList, setLedMachineList } from 'redux/slices/groupSlice';
import { MachineListType } from 'types/group/GroupType';
import COMMON from 'constants/common';
import TooltipComponent from 'components/system/organisms/dialogs/TooltipComponent';
import { getStationAndGiftName, renderMachineType } from 'utils/common.helper';
import MachineStationTableHead from 'components/data/organisms/tables/common/MachineStationTableHead';

const column1 = 229;
const column2 = 216;
/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  default: {
    height: '332px',
    width: 1100,
    overflowX: 'hidden',
  },
  table: {
    tableLayout: 'fixed',
    borderCollapse: 'separate', // collapseにするとスクロール時にヘッダーのborderが消えるので注意
    borderLeft: '1px solid #707070',
    '& th,td': {
      fontSize: '23px',
      lineHeight: '24px',
      padding: '0 16px',
    },
    '& th': {
      textAlign: 'center',
      height: '48px',
      padding: 0,
      backgroundColor: '#CFCFCF',
      borderTop: '1px solid #707070',
      borderBottom: '1px solid #707070',
      borderRight: '1px solid #707070',
    },
    '& tbody tr': {
      backgroundColor: '#FFFFFF',
      '&:hover': {
        cursor: 'pointer',
      },
    },
    '& td': {
      minHeight: '76px',
      borderBottom: '1px solid #707070',
      borderRight: '1px solid #707070',
      padding: '10px 16px',
      '&.cellStyle': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      },
      '&.multiMachineNo': {
        verticalAlign: 'top',
        padding: '20px 16px',
      },
      '&.multiMachineName': {
        verticalAlign: 'top',
        padding: '28px 4px',
      },
      '&.baseMachineName': {
        padding: '10px 4px',
      },
    },
  },
  trSelected: {
    position: 'relative',
    zIndex: 1350,
  },
  tdMachineName: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
  orderNumber: {
    height: '40px',
    width: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    backgroundColor: '#535B80',
    color: '#FFFFFF',
    flexShrink: 0,
  },
  tdStationName: {
    textAlign: 'left',
  },
  rightStationNameWrap: {
    paddingTop: 4,
  },
}));
/* ************ Type ************ */
type Props = {
  selectedOrderNo?: number;
  groupCategory: number;
  rows: MachineListType[];
  handleClickRow: (
    ev: React.MouseEvent<HTMLTableRowElement>,
    orderNo: number,
    rows: MachineListType[],
    handleClickNewOrderNo: (newOrderNo: number, oldOrderNo: number, machineList: MachineListType[]) => void,
  ) => void;
  className?: string;
};
/* ************ Component ************ */
const OrderSettingTable: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  groupCategory,
  rows,
  handleClickRow,
  selectedOrderNo,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  /* ************ Events ************ */

  // click new order no
  const handleClickNewOrderNo = (newOrderNo: number, oldOrderNo: number, machineList: MachineListType[]) => {
    if (newOrderNo === oldOrderNo) return; // 連動順に変更がない場合

    let newMList = [];
    if (newOrderNo > oldOrderNo) {
      // 連動順が上位になるケース
      newMList = machineList.map((m) => {
        // 選択した行は新連動順を設定
        if (oldOrderNo === m.orderNo) return { ...m, orderNo: newOrderNo };

        // 選択した行より上位の機器の連動順は維持
        if (oldOrderNo > m.orderNo) return m;

        // 選択した行の旧連動順から新連動順までの間に存在する機器の連動順は繰り上げ
        if (oldOrderNo < m.orderNo && m.orderNo <= newOrderNo) return { ...m, orderNo: m.orderNo - 1 };

        // 新連動順より下位の機器の連動順は維持
        return m;
      });
    } else {
      // 連動順が下位になるケース
      newMList = machineList.map((m) => {
        // 選択した行は新連動順を設定
        if (oldOrderNo === m.orderNo) return { ...m, orderNo: newOrderNo };

        // 新連動順より上位の機器の連動順は維持
        if (newOrderNo > m.orderNo) return m;

        // 新連動順より下位の機器から選択した行の旧連動順までの間に存在する機器は繰り下げ
        if (newOrderNo <= m.orderNo && m.orderNo < oldOrderNo) return { ...m, orderNo: m.orderNo + 1 };

        // 選択した行より下位の機器の連動順は維持
        return m;
      });
    }

    // 再並び替え
    newMList.sort((a, b) => {
      if (a.orderNo > b.orderNo) {
        return 1;
      }
      return -1;
    });

    if (groupCategory === COMMON.GROUP_CATEGORY.LED) {
      dispatch(setLedMachineList(newMList));
    } else {
      dispatch(setMachineList(newMList));
    }
  };

  // テーブル列定義
  const headerColumns = [
    { id: 'boardSerial', title: '登録番号', width: column1 },
    { id: 'machineType', title: '機種名', width: column2 },
    { id: 'stationNameAndGiftName', title: 'ステーション名 景品名', width: '100%' },
  ];

  const renderStationName = React.useCallback((row: MachineListType) => {
    let content: JSX.Element = <></>;
    switch (row.machineType) {
      case COMMON.MACHINE_TYPE.CLENA3:
      case COMMON.MACHINE_TYPE.DUO:
        content = (
          <>
            <TooltipComponent
              tooltipText={row.giftNameL}
              displayWord={getStationAndGiftName(
                row.machineType,
                row.stationNameL,
                COMMON.LEFT_RIGHT.LEFT,
                row.giftNameL,
              )}
            />
            <div className={classes.rightStationNameWrap}>
              <TooltipComponent
                tooltipText={row.giftNameR}
                displayWord={getStationAndGiftName(
                  row.machineType,
                  row.stationNameR,
                  COMMON.LEFT_RIGHT.RIGHT,
                  row.giftNameR,
                )}
              />
            </div>
          </>
        );
        break;
      case COMMON.MACHINE_TYPE.MULTI:
        content = (
          <>
            <TooltipComponent
              tooltipText={row.giftNameSt1}
              displayWord={getStationAndGiftName(
                row.machineType,
                row.stationNameSt1 ?? '',
                COMMON.LEFT_RIGHT.LEFT,
                row.giftNameSt1,
                COMMON.POSITION.ST1,
              )}
            />
            <div className={classes.rightStationNameWrap}>
              <TooltipComponent
                tooltipText={row.giftNameSt2}
                displayWord={getStationAndGiftName(
                  row.machineType,
                  row.stationNameSt2 ?? '',
                  COMMON.LEFT_RIGHT.RIGHT,
                  row.giftNameSt2,
                  COMMON.POSITION.ST2,
                )}
              />
            </div>
            <div className={classes.rightStationNameWrap}>
              <TooltipComponent
                tooltipText={row.giftNameSt3}
                displayWord={getStationAndGiftName(
                  row.machineType,
                  row.stationNameSt3 ?? '',
                  COMMON.LEFT_RIGHT.LEFT,
                  row.giftNameSt3,
                  COMMON.POSITION.ST3,
                )}
              />
            </div>
            <div className={classes.rightStationNameWrap}>
              <TooltipComponent
                tooltipText={row.giftNameSt4}
                displayWord={getStationAndGiftName(
                  row.machineType,
                  row.stationNameSt4 ?? '',
                  COMMON.LEFT_RIGHT.RIGHT,
                  row.giftNameSt4,
                  COMMON.POSITION.ST4,
                )}
              />
            </div>
          </>
        );
        break;
      default:
        content = (
          <TooltipComponent
            tooltipText={row.giftNameL}
            displayWord={getStationAndGiftName(
              row.machineType,
              row.stationNameL,
              COMMON.LEFT_RIGHT.LEFT,
              row.giftNameL,
            )}
          />
        );
    }
    return content;
  }, []);

  return (
    <TableContainer id="scrollContainer" className={className || classes.default}>
      <Table className={classes.table} stickyHeader aria-label="sticky table">
        <MachineStationTableHead columns={headerColumns} />
        <TableBody>
          {rows.map((row) => {
            const isSelected = row.orderNo === selectedOrderNo;
            return (
              <TableRow
                className={isSelected ? classes.trSelected : ''}
                key={row.boardSerial}
                onClick={(event) => handleClickRow(event, row.orderNo, rows, handleClickNewOrderNo)}>
                <TableCell
                  className={`${
                    row.machineType === COMMON.MACHINE_TYPE.MULTI ? 'multiMachineNo' : classes.tdMachineName
                  }`}>
                  <div className={`cellStyle ${classes.tdMachineName}`}>
                    <span className={classes.orderNumber}>{row.orderNo}</span>
                    <div>{row.boardSerial}</div>
                  </div>
                </TableCell>
                <TableCell
                  className={`${classes.tdStationName} ${
                    row.machineType === COMMON.MACHINE_TYPE.MULTI ? 'multiMachineName' : 'baseMachineName'
                  } cellStyle`}>
                  {renderMachineType(row.machineType)}
                </TableCell>
                <TableCell className={classes.tdStationName}>{renderStationName(row)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default memo(OrderSettingTable);
